import { useEffect, useSyncExternalStore } from 'react';

import { ApiClientError } from '../../core/api';
import {
  EMPTY_ENTITLEMENTS,
  loadSubscriptionSnapshot,
} from './api';
import type { EntitlementSnapshot, PlanCapabilities } from './api';

const CACHE_MS = 60_000;
let current: EntitlementSnapshot = EMPTY_ENTITLEMENTS;
let loadedAt = 0;
let pending: Promise<EntitlementSnapshot> | null = null;
let accountId: string | null | undefined;
let generation = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function getEntitlements(): EntitlementSnapshot {
  return current;
}

export function entitlementAllows(capability: keyof PlanCapabilities): boolean {
  return current.active && current.capabilities[capability];
}

export function subscribeEntitlements(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setEntitlementsForTests(value: EntitlementSnapshot): void {
  generation += 1;
  pending = null;
  current = value;
  loadedAt = Date.now();
  emit();
}

export async function loadEntitlements(force = false): Promise<EntitlementSnapshot> {
  if (!force && loadedAt > 0 && Date.now() - loadedAt < CACHE_MS) return current;
  if (pending !== null) return pending;
  const epoch = generation;
  const task = (async () => {
    let next = EMPTY_ENTITLEMENTS;
    try {
      next = await loadSubscriptionSnapshot();
    } catch (error) {
      if (!(error instanceof ApiClientError && error.status === 401)) {
        // Keep the UI locked, matching the server-as-source-of-truth rule.
      }
    }
    if (epoch === generation) {
      current = next;
      loadedAt = Date.now();
      emit();
    }
    return current;
  })();
  pending = task;
  void task.finally(() => { if (pending === task) pending = null; });
  return task;
}

/** Refresh before a runtime privilege decision while avoiding a request on every timer tick. */
export async function ensureFreshEntitlements(maxAgeMs = CACHE_MS): Promise<EntitlementSnapshot> {
  if (loadedAt > 0 && Date.now() - loadedAt <= maxAgeMs) return current;
  return loadEntitlements(true);
}

export function invalidateEntitlements(): void {
  loadedAt = 0;
  void loadEntitlements(true);
}

if (typeof window !== 'undefined') {
  window.addEventListener('zhixin:account-change', (event: Event) => {
    const next = (event as CustomEvent<{ userId: string | null }>).detail.userId;
    if (next === accountId) return;
    accountId = next;
    // Requests made with the old session must never update the new account.
    generation += 1;
    pending = null;
    loadedAt = 0;
    current = EMPTY_ENTITLEMENTS;
    emit();
    if (next !== null) void loadEntitlements(true);
  });
}

export function useEntitlements(): EntitlementSnapshot {
  const snapshot = useSyncExternalStore(subscribeEntitlements, getEntitlements, getEntitlements);
  useEffect(() => {
    void loadEntitlements();
  }, []);
  return snapshot;
}
